import { captureHandledException } from '@/lib/sentry';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FlaskConical, Search, Plus, Edit, XCircle, ArrowRightCircle } from 'lucide-react';
import { useAuth } from '@/contexts/use-auth';
import { useToast } from '@/hooks/use-toast';
import { getCompanies, updateCompany, getAppUsersByCompany, updateAppUser, writeCompanyAudit, Timestamp } from '@/lib/firebase';
import AddTrialModal from '@/components/add-trial-modal';
import EditTrialModal from '@/components/edit-trial-modal';
import ConvertToLiveModal from '@/components/convert-to-live-modal';
import { logger } from '@/lib/logger';

interface TrialCompany {
  id: string;
  name: string;
  type?: string;
  status?: string;
  trialExpiryDate?: any;
  created?: any;
}

const formatDate = (ts: any): string => {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (error) {
    captureHandledException(error, "trials.parse-date");
    return '—';
  }
};

const isActive = (ts: any): boolean => {
  if (!ts) return false;
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d > new Date();
  } catch (error) {
    captureHandledException(error, "trials.parse-date");
    return false;
  }
};

export default function Trials() {
  const [trialCompanies, setTrialCompanies] = useState<TrialCompany[]>([]);
  const [filteredTrials, setFilteredTrials] = useState<TrialCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<TrialCompany | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (!user) return;
    const loadData = async () => {
      try {
        logger.log('🧪 Loading trial companies...');
        const all = await getCompanies();
        const trials = all.filter((c: any) => c.trialExpiryDate != null) as TrialCompany[];
        setTrialCompanies(trials);
      } catch (error: any) {
        logger.error('❌ Error loading trial companies:', error);
        toast({ title: "Failed to load trials", description: error.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    loadData();
    const timer = setInterval(loadData, 15_000);
    return () => clearInterval(timer);
  }, [user, toast]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTrials(trialCompanies);
    } else {
      setFilteredTrials(
        trialCompanies.filter(c =>
          c.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [trialCompanies, searchTerm]);

  const handleEdit = (company: TrialCompany) => {
    setSelectedCompany(company);
    setEditModalOpen(true);
  };

  const handleConvert = (company: TrialCompany) => {
    setSelectedCompany(company);
    setConvertModalOpen(true);
  };

  const handleCancelTrial = async (company: TrialCompany) => {
    if (!confirm(`Cancel the trial for "${company.name}"? This will set the trial expiry to now for the company and all associated users.`)) {
      return;
    }

    setCancellingId(company.id);
    try {
      const now = Timestamp.now();

      await updateCompany(company.id, { trialExpiryDate: now });

      await writeCompanyAudit(
        company.id,
        company.name,
        'trial_cancelled',
        user?.email ?? 'unknown',
        user?.uid ?? '',
        [{ field: 'trialExpiryDate', from: formatDate(company.trialExpiryDate), to: 'cancelled' }]
      );

      const appUsers = await getAppUsersByCompany(company.id);
      await Promise.all(appUsers.map(u => updateAppUser(u.id, { trialExpiryDate: now })));

      logger.log('✅ Trial cancelled for company:', company.id, '| appUsers updated:', appUsers.length);

      toast({
        title: "Trial cancelled",
        description: `The trial for "${company.name}" has been cancelled.`,
      });
    } catch (error: any) {
      logger.error('❌ Error cancelling trial:', error);
      toast({
        title: "Failed to cancel trial",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCancellingId(null);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'partner': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'customer': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading trials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FlaskConical className="h-6 w-6" />
              Trials
            </h1>
            <p className="text-slate-600 mt-1">
              Manage company trial accounts and their expiry dates
            </p>
          </div>
          <Button
            className="flex items-center gap-2"
            onClick={() => setAddModalOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add Trial
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                placeholder="Search by company name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Results summary */}
        <div className="text-sm text-slate-600">
          Showing {filteredTrials.length} of {trialCompanies.length} trial{trialCompanies.length !== 1 ? 's' : ''}
        </div>

        {/* Trials Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Trial Companies</CardTitle>
            <CardDescription>
              Companies with an active or expired trial period
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredTrials.length === 0 ? (
              <div className="text-center py-12">
                <FlaskConical className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {trialCompanies.length === 0 ? 'No trial companies yet' : 'No matching trials'}
                </h3>
                <p className="text-slate-600 mb-4">
                  {trialCompanies.length === 0
                    ? 'Get started by adding your first trial company.'
                    : 'Try adjusting your search term.'}
                </p>
                {trialCompanies.length === 0 && (
                  <Button
                    className="flex items-center gap-2"
                    onClick={() => setAddModalOpen(true)}
                  >
                    <Plus className="h-4 w-4" />
                    Add First Trial
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Trial Expiry</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTrials.map((company) => {
                      const active = isActive(company.trialExpiryDate);
                      const cancelling = cancellingId === company.id;
                      return (
                        <TableRow key={company.id}>
                          <TableCell>
                            <div className="font-medium">{company.name || 'Unnamed Company'}</div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getTypeColor(company.type || '')}>
                              {company.type || 'Customer'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-700">
                            {formatDate(company.trialExpiryDate)}
                          </TableCell>
                          <TableCell>
                            {active ? (
                              <Badge className="bg-green-100 text-green-800 border-green-200">
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 border-red-200">
                                Expired
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleEdit(company)}
                                disabled={cancelling}
                                title="Edit trial"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 gap-1.5 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 text-xs font-medium"
                                onClick={() => handleConvert(company)}
                                disabled={cancelling}
                              >
                                <ArrowRightCircle className="h-3.5 w-3.5" />
                                Convert to Live
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleCancelTrial(company)}
                                disabled={cancelling || !active}
                                title={active ? "Cancel trial" : "Trial already expired"}
                              >
                                {cancelling ? (
                                  <div className="h-4 w-4 animate-spin border-2 border-red-600 border-t-transparent rounded-full" />
                                ) : (
                                  <XCircle className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AddTrialModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
      />

      {selectedCompany && (
        <EditTrialModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedCompany(null);
          }}
          company={selectedCompany}
        />
      )}

      {selectedCompany && (
        <ConvertToLiveModal
          isOpen={convertModalOpen}
          onClose={() => {
            setConvertModalOpen(false);
            setSelectedCompany(null);
          }}
          company={selectedCompany}
        />
      )}
    </div>
  );
}
